/**
 * Performance monitoring utilities
 * Provides Core Web Vitals tracking, performance threshold evaluation,
 * and custom performance measurement tools
 */

import { onCLS, onFCP, onLCP, onTTFB, onINP } from 'web-vitals';

// Performance thresholds based on Google's Core Web Vitals recommendations
export const PERFORMANCE_THRESHOLDS = {
	LCP: 2500,  // Largest Contentful Paint (ms) - Good: <=2500, Needs Improvement: <=4000, Poor: >4000
	FID: 100,   // First Input Delay (ms) - Good: <=100, Needs Improvement: <=300, Poor: >300
	INP: 200,   // Interaction to Next Paint (ms) - Good: <=200, Needs Improvement: <=500, Poor: >500
	CLS: 0.1,   // Cumulative Layout Shift - Good: <=0.1, Needs Improvement: <=0.25, Poor: >0.25
	FCP: 1800,  // First Contentful Paint (ms) - Good: <=1800, Needs Improvement: <=3000, Poor: >3000
	TTFB: 800,  // Time to First Byte (ms) - Good: <=800, Needs Improvement: <=1800, Poor: >1800
	COMPONENT_RENDER: 16, // Component render time (ms) - Target: 60fps
	API_CALL: 5000,       // API call timeout (ms)
};

// Performance rating levels
export const PERFORMANCE_RATING = {
	GOOD: 'good',
	NEEDS_IMPROVEMENT: 'needs-improvement',
	POOR: 'poor',
	UNKNOWN: 'unknown',
};

// Store collected metrics
const metricsStore = new Map();
const metricCallbacks = new Set();

/**
 * Evaluate performance metric against threshold
 * @param {string} metricName - Name of the metric (LCP, FID, etc.)
 * @param {number} value - Metric value
 * @returns {string} Performance rating: 'good', 'needs-improvement', 'poor', or 'unknown'
 */
export const evaluatePerformance = (metricName, value) => {
	const threshold = PERFORMANCE_THRESHOLDS[metricName];
	if (!threshold || value === undefined || value === null) {
		return PERFORMANCE_RATING.UNKNOWN;
	}

	// For CLS, lower is better - reverse the logic
	if (metricName === 'CLS') {
		if (value <= threshold) return PERFORMANCE_RATING.GOOD;
		if (value <= threshold * 2.5) return PERFORMANCE_RATING.NEEDS_IMPROVEMENT;
		return PERFORMANCE_RATING.POOR;
	}

	// For all other metrics, lower is better
	if (value <= threshold) return PERFORMANCE_RATING.GOOD;
	if (value <= threshold * 1.5) return PERFORMANCE_RATING.NEEDS_IMPROVEMENT;
	return PERFORMANCE_RATING.POOR;
};

/**
 * Get performance rating for a metric
 * @param {Object} metric - Metric object with name and value
 * @returns {string} Performance rating
 */
export const getPerformanceRating = (metric) => {
	if (!metric || !metric.name) return PERFORMANCE_RATING.UNKNOWN;
	return evaluatePerformance(metric.name, metric.value);
};

/**
 * Store metric for later analysis
 * @param {Object} metric - Metric object
 */
export const storeMetric = (metric) => {
	const metricKey = metric.name || 'unknown';
	if (!metricsStore.has(metricKey)) {
		metricsStore.set(metricKey, []);
	}
	metricsStore.get(metricKey).push({
		...metric,
		timestamp: Date.now(),
		url: window.location.href,
	});

	// Keep only last 100 metrics per type
	const stored = metricsStore.get(metricKey);
	if (stored.length > 100) {
		stored.shift();
	}
};

/**
 * Subscribe to metric updates
 * @param {Function} callback - Callback function to receive metrics
 * @returns {Function} Unsubscribe function
 */
export const subscribeToMetrics = (callback) => {
	metricCallbacks.add(callback);
	return () => {
		metricCallbacks.delete(callback);
	};
};

/**
 * Notify all subscribers of a new metric
 * @param {Object} metric - Metric object
 */
const notifySubscribers = (metric) => {
	metricCallbacks.forEach((callback) => {
		try {
			callback(metric);
		} catch (error) {
			console.error('[performance][notifySubscribers] - Error in metric callback:', error);
		}
	});
};

/**
 * Send metric to analytics/logging services
 * @param {Object} metric - Metric object
 */
const sendMetricToAnalytics = (metric) => {
	const rating = getPerformanceRating(metric);

	// Store metric
	storeMetric({ ...metric, rating });

	// Log in development
	if (process.env.NODE_ENV === 'development') {
		const ratingSymbol = {
			[PERFORMANCE_RATING.GOOD]: '✓',
			[PERFORMANCE_RATING.NEEDS_IMPROVEMENT]: '⚠',
			[PERFORMANCE_RATING.POOR]: '✗',
		}[rating] || '?';

		console.log(
			`[performance][${metric.name}] ${ratingSymbol} ${metric.value.toFixed(2)}${getMetricUnit(metric.name)} (${rating})`
		);
	}

	// Send to Google Analytics if available
	if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
		window.gtag('event', metric.name, {
			value: Math.round(metric.value),
			event_category: 'Web Vitals',
			event_label: metric.id,
			non_interaction: true,
			custom_map: {
				rating,
			},
		});
	}

	// Notify subscribers
	notifySubscribers({ ...metric, rating });
};

/**
 * Get unit for metric
 * @param {string} metricName - Metric name
 * @returns {string} Unit string
 */
const getMetricUnit = (metricName) => {
	if (metricName === 'CLS') return '';
	return 'ms';
};

/**
 * Initialize Core Web Vitals tracking
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable tracking (default: true in production, true in dev if env var set)
 * @param {Function} [options.onMetric] - Optional custom callback for metrics
 */
export const initCoreWebVitals = (options = {}) => {
	const enabled =
		options.enabled !== undefined
			? options.enabled
			: process.env.NODE_ENV === 'production' ||
			  process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === 'true';

	if (!enabled) {
		console.log('[performance][initCoreWebVitals] - Performance monitoring disabled');
		return;
	}

	try {
		// Track Largest Contentful Paint
		onLCP((metric) => {
			sendMetricToAnalytics(metric);
			if (options.onMetric) options.onMetric(metric);
		});

		// Track Interaction to Next Paint (replaces FID)
		onINP((metric) => {
			sendMetricToAnalytics(metric);
			if (options.onMetric) options.onMetric(metric);
		});

		// Track First Contentful Paint
		onFCP((metric) => {
			sendMetricToAnalytics(metric);
			if (options.onMetric) options.onMetric(metric);
		});

		// Track Cumulative Layout Shift
		onCLS((metric) => {
			sendMetricToAnalytics(metric);
			if (options.onMetric) options.onMetric(metric);
		});

		// Track Time to First Byte
		onTTFB((metric) => {
			sendMetricToAnalytics(metric);
			if (options.onMetric) options.onMetric(metric);
		});

		console.log('[performance][initCoreWebVitals] - Core Web Vitals tracking initialized');
	} catch (error) {
		console.error('[performance][initCoreWebVitals] - Error initializing Core Web Vitals:', error);
	}
};

/**
 * Create a performance mark
 * @param {string} name - Mark name
 * @returns {void}
 */
export const mark = (name) => {
	if (typeof performance !== 'undefined' && performance.mark) {
		performance.mark(name);
	}
};

/**
 * Create a performance measure
 * @param {string} name - Measure name
 * @param {string} startMark - Start mark name
 * @param {string} endMark - End mark name (optional, defaults to now)
 * @returns {PerformanceEntry|null} Performance entry or null
 */
export const measure = (name, startMark, endMark) => {
	if (typeof performance === 'undefined' || !performance.measure) {
		return null;
	}

	try {
		if (endMark) {
			performance.measure(name, startMark, endMark);
		} else {
			performance.measure(name, startMark);
		}

		const entries = performance.getEntriesByName(name, 'measure');
		return entries.length > 0 ? entries[entries.length - 1] : null;
	} catch (error) {
		console.error(`[performance][measure] - Error measuring ${name}:`, error);
		return null;
	}
};

/**
 * Measure execution time of a function
 * @param {string} name - Measurement name
 * @param {Function} fn - Function to measure
 * @returns {*} Function result
 */
export const measureExecution = (name, fn) => {
	const startMark = `${name}-start`;
	const endMark = `${name}-end`;

	mark(startMark);
	const result = typeof fn === 'function' ? fn() : fn;
	mark(endMark);

	const entry = measure(name, startMark, endMark);

	if (entry && process.env.NODE_ENV === 'development') {
		const duration = entry.duration;
		if (duration > PERFORMANCE_THRESHOLDS.COMPONENT_RENDER) {
			console.warn(`[performance][${name}] - Slow execution: ${duration.toFixed(2)}ms`);
		}
	}

	// Clean up marks
	if (performance.clearMarks) {
		performance.clearMarks(startMark);
		performance.clearMarks(endMark);
	}

	return result;
};

/**
 * Measure async execution time
 * @param {string} name - Measurement name
 * @param {Function} fn - Async function to measure
 * @returns {Promise<*>} Function result
 */
export const measureAsyncExecution = async (name, fn) => {
	const startMark = `${name}-start`;
	const endMark = `${name}-end`;

	mark(startMark);
	try {
		const result = await fn();
		mark(endMark);

		const entry = measure(name, startMark, endMark);

		if (entry && process.env.NODE_ENV === 'development') {
			const duration = entry.duration;
			if (duration > PERFORMANCE_THRESHOLDS.API_CALL) {
				console.warn(`[performance][${name}] - Slow async execution: ${duration.toFixed(2)}ms`);
			}
		}

		return result;
	} catch (error) {
		mark(endMark);
		measure(name, startMark, endMark);
		throw error;
	} finally {
		// Clean up marks
		if (performance.clearMarks) {
			performance.clearMarks(startMark);
			performance.clearMarks(endMark);
		}
	}
};

/**
 * Get all stored metrics
 * @param {string} metricName - Optional metric name filter
 * @returns {Array|Map} Array of metrics or Map of all metrics
 */
export const getStoredMetrics = (metricName) => {
	if (metricName) {
		return metricsStore.get(metricName) || [];
	}
	return metricsStore;
};

/**
 * Clear stored metrics
 * @param {string} metricName - Optional metric name to clear
 */
export const clearStoredMetrics = (metricName) => {
	if (metricName) {
		metricsStore.delete(metricName);
	} else {
		metricsStore.clear();
	}
};

/**
 * Get performance summary
 * @returns {Object} Performance summary with latest metrics and ratings
 */
export const getPerformanceSummary = () => {
	const summary = {};
	const metricNames = ['LCP', 'FID', 'INP', 'CLS', 'FCP', 'TTFB'];

	metricNames.forEach((name) => {
		const metrics = metricsStore.get(name) || [];
		if (metrics.length > 0) {
			const latest = metrics[metrics.length - 1];
			const allValues = metrics.map((m) => m.value);
			summary[name] = {
				latest: latest.value,
				rating: latest.rating || evaluatePerformance(name, latest.value),
				average: allValues.reduce((a, b) => a + b, 0) / allValues.length,
				min: Math.min(...allValues),
				max: Math.max(...allValues),
				count: metrics.length,
			};
		}
	});

	return summary;
};

